import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { PageHeader, Card, Button, Alert } from '../components/ui';
import adminUserService from '../services/adminUserService';
import { useAuth } from '../context/AuthContext';

const initialFormState = {
  username: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'admin'
};

const AdminUsers = () => {
  const [formData, setFormData] = useState(initialFormState);
  const [formError, setFormError] = useState('');
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();

  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery('admin-users', adminUserService.getAdminUsers);

  const adminUsers = Array.isArray(data) ? data : [];
  const dataError = data && !Array.isArray(data);

  const createUserMutation = useMutation(
    (payload) => adminUserService.createAdminUser(payload),
    {
      onSuccess: () => {
        toast.success('Admin user created successfully');
        queryClient.invalidateQueries('admin-users');
        setFormData(initialFormState);
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to create admin user';
        toast.error(message);
      }
    }
  );

  const resetPasswordMutation = useMutation(
    ({ id, password }) => adminUserService.resetPassword(id, password),
    {
      onSuccess: () => {
        toast.success('Password updated successfully');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to reset password';
        toast.error(message);
      }
    }
  );

  const deleteUserMutation = useMutation(
    (id) => adminUserService.deleteAdminUser(id),
    {
      onSuccess: () => {
        toast.success('Admin user deleted successfully');
        queryClient.invalidateQueries('admin-users');
      },
      onError: (error) => {
        const message = error.response?.data?.message || 'Failed to delete admin user';
        toast.error(message);
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateUser = (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.username.trim() || !formData.password.trim()) {
      setFormError('Username and password are required');
      return;
    }

    if (formData.password.length < 8) {
      setFormError('Password must be at least 8 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    createUserMutation.mutate({
      username: formData.username.trim(),
      password: formData.password,
      email: formData.email.trim() || null,
      role: formData.role
    });
  };

  const handleResetPassword = (user) => {
    const newPassword = window.prompt(`Введите новый пароль для пользователя "${user.username}" (минимум 8 символов):`);
    if (!newPassword) {
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Пароль должен содержать минимум 8 символов');
      return;
    }

    resetPasswordMutation.mutate({ id: user.id, password: newPassword });
  };

  const handleDeleteUser = (user) => {
    if (!window.confirm(`Удалить администратора "${user.username}"?`)) {
      return;
    }
    deleteUserMutation.mutate(user.id);
  };

  return (
    <div>
      <PageHeader
        title="Admin Users"
        description="Create additional administrator accounts and manage existing ones"
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Create New Admin">
          {formError && (
            <Alert
              variant="error"
              className="mb-4"
              isDismissible
              onDismiss={() => setFormError('')}
            >
              {formError}
            </Alert>
          )}

          <form className="space-y-4" onSubmit={handleCreateUser}>
            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="username">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Имя пользователя"
                disabled={createUserMutation.isLoading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="email">
                Email (optional)
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="admin@example.com"
                disabled={createUserMutation.isLoading}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Минимум 8 символов"
                  disabled={createUserMutation.isLoading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700" htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="Повторите пароль"
                  disabled={createUserMutation.isLoading}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700" htmlFor="role">
                Role
              </label>
              <select
                id="role"
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                disabled={createUserMutation.isLoading}
              >
                <option value="admin">Admin</option>
              </select>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                variant="primary"
                isLoading={createUserMutation.isLoading}
                disabled={createUserMutation.isLoading}
              >
                Create Admin User
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Existing Admins">
          {isLoading ? (
            <p className="text-sm text-gray-500">Loading admin users...</p>
          ) : isError || dataError ? (
            <Alert variant="error">
              {error?.response?.data?.message || 'Не удалось загрузить администраторов'}
            </Alert>
          ) : adminUsers.length === 0 ? (
            <p className="text-sm text-gray-500">Администраторы ещё не созданы.</p>
          ) : (
            <div className="space-y-4">
              {adminUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex flex-col gap-2 rounded-md border border-gray-200 p-4 md:flex-row md:items-center md:justify-between"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{user.username}</p>
                    <p className="text-xs text-gray-500">{user.email || '—'}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Role: {user.role || 'admin'} • Created:{' '}
                      {user.created_at ? new Date(user.created_at).toLocaleString() : '—'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleResetPassword(user)}
                      isLoading={resetPasswordMutation.isLoading}
                      disabled={resetPasswordMutation.isLoading}
                    >
                      Reset Password
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteUser(user)}
                      isLoading={deleteUserMutation.isLoading}
                      disabled={
                        deleteUserMutation.isLoading ||
                        adminUsers.length <= 1 ||
                        user.id === currentUser?.id
                      }
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdminUsers;
